import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '#/shared/lib/utils'

/** Splits on `{{merge_field}}` so the tokens can be inked apart from the copy. */
const MERGE_FIELD_SPLIT = /(\{\{[a-z_]+\}\})/g
/** Separate, non-global twin: `test` on a `/g` regex carries `lastIndex`. */
const MERGE_FIELD = /^\{\{[a-z_]+\}\}$/

/**
 * Typography and box shared by the textarea and the layer painted behind it.
 * The two MUST agree to the pixel — the highlight is only in register because
 * both wrap identically, and the floor has to be on both or the short one stops
 * driving the wrapper's height.
 */
const SHARED_BOX =
  'min-h-20 w-full rounded-md border border-transparent px-2.5 py-2 text-sm leading-5.5 whitespace-pre-wrap'

function HighlightedText({ text }: { text: string }) {
  return (
    <>
      {text.split(MERGE_FIELD_SPLIT).map((part, index) => (
        <Fragment key={index}>
          {MERGE_FIELD.test(part) ? (
            <span className="text-campaign-running">{part}</span>
          ) : (
            part
          )}
        </Fragment>
      ))}
    </>
  )
}

interface CampaignMessageEditorProps {
  value: string
  onChange: (next: string) => void
  /** Announced to assistive tech — the field has no visible label of its own. */
  label: string
  className?: string
}

/**
 * The message body, editable in place.
 *
 * A plain textarea cannot ink its own merge fields, so the highlighted copy is
 * painted on a layer underneath and the textarea sits on top with transparent
 * text and a visible caret. The field grows to its content on every keystroke,
 * which keeps the two layers the same height and means neither ever scrolls —
 * the usual reason this technique falls out of register.
 */
export function CampaignMessageEditor({
  value,
  onChange,
  label,
  className,
}: CampaignMessageEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const [focused, setFocused] = useState(false)

  const autosize = useCallback(() => {
    const node = ref.current
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }, [])

  useLayoutEffect(autosize, [autosize, value])

  return (
    <div
      className={cn(
        'relative rounded-md border transition-colors',
        focused ? 'border-gray-200 bg-white' : 'border-transparent',
        className,
      )}
    >
      <div
        aria-hidden="true"
        className={cn(SHARED_BOX, 'pointer-events-none text-gray-900')}
      >
        <HighlightedText text={value} />
        {/* A trailing newline collapses in the layer but not in the field —
            this keeps both boxes the same height. */}
        {value.endsWith('\n') && ' '}
      </div>
      <textarea
        ref={ref}
        value={value}
        aria-label={label}
        spellCheck
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          SHARED_BOX,
          'absolute inset-0 resize-none overflow-hidden bg-transparent text-transparent caret-gray-900 outline-none',
        )}
      />
    </div>
  )
}
